import { Component } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { portalCommands } from 'src/app/utils/portal-path';
import { BusCardsPdfService } from 'src/app/services/bus-cards-pdf.service';
import { BusesService } from 'src/app/services/buses.service';
import { LanguageService } from 'src/app/services/language.service';
import {
  DEFAULT_STUDENT_CARD_COLORS,
  SchoolSettingsService,
} from 'src/app/services/school-settings.service';

@Component({
  selector: 'app-buses',
  templateUrl: './buses.component.html',
  styleUrls: ['./buses.component.css']
})
export class BusesComponent {
  items: any[] = [];
  filteredItems: any[] = [];
  selected: any[] = [];
  searchTerm = '';
  pageSize = 10;
  pageIndex = 0;
  pageSizeOptions = [10, 20, 50];
  showDeleteModal = false;
  deleteModalBody = '';
  deleteModalParams: Record<string, unknown> = {};
  open = false;
  mode = 'add';
  selectedId = '';
  isError = false;
  errorMessage = '';
  exportingCards = false;
  private schoolName = '';
  private cutMarginMm = DEFAULT_STUDENT_CARD_COLORS.cutMarginMm;

  form = new FormGroup({
    name: new FormControl('', [Validators.required]),
    number: new FormControl('', [Validators.required]),
    plateNumber: new FormControl(''),
    driverName: new FormControl(''),
    driverPhone: new FormControl(''),
    capacity: new FormControl<number | null>(null),
  });

  constructor(
    private busesService: BusesService,
    private router: Router,
    private languageService: LanguageService,
    private translate: TranslateService,
    private schoolSettingsService: SchoolSettingsService,
    private busCardsPdfService: BusCardsPdfService,
  ) {}

  async ngOnInit() {
    await Promise.all([this.fetchItems(), this.loadSchoolSettings()]);
  }

  private async loadSchoolSettings() {
    try {
      const settings = await this.schoolSettingsService.get();
      this.schoolName = settings?.schoolName || '';
      this.cutMarginMm = settings?.studentCardColors?.cutMarginMm
        ?? DEFAULT_STUDENT_CARD_COLORS.cutMarginMm;
    } catch {
      this.schoolName = '';
      this.cutMarginMm = DEFAULT_STUDENT_CARD_COLORS.cutMarginMm;
    }
  }

  get exporting(): boolean {
    return this.exportingCards;
  }

  async fetchItems() {
    this.items = await this.busesService.list();
    this.applyFilter();
  }

  get paginatedItems() {
    const start = this.pageIndex * this.pageSize;
    return this.filteredItems.slice(start, start + this.pageSize);
  }
  get totalPages() { return Math.max(1, Math.ceil(this.filteredItems.length / this.pageSize)); }
  get pageStart() { return this.filteredItems.length ? this.pageIndex * this.pageSize + 1 : 0; }
  get pageEnd() { return Math.min((this.pageIndex + 1) * this.pageSize, this.filteredItems.length); }

  applyFilter() {
    const term = this.searchTerm.trim().toLowerCase();
    this.filteredItems = term
      ? this.items.filter(item => {
          const haystack = `${item.name || ''} ${item.number || ''} ${item.driverName || ''} ${item.plateNumber || ''}`.toLowerCase();
          return haystack.includes(term);
        })
      : [...this.items];
    this.pageIndex = 0;
  }

  onSearchChange() { this.applyFilter(); }
  onPageSizeChange(size: string) { this.pageSize = Number(size); this.pageIndex = 0; }
  previousPage() { if (this.pageIndex > 0) this.pageIndex--; }
  nextPage() { if (this.pageIndex < this.totalPages - 1) this.pageIndex++; }
  isSelected(item: any) { return this.selected.some(s => s.id === item.id); }
  toggleSelection(item: any, event: Event) {
    event.stopPropagation();
    this.selected = this.isSelected(item) ? this.selected.filter(s => s.id !== item.id) : [...this.selected, item];
  }
  clearSelection() { this.selected = []; }

  async printCardsPdf(): Promise<void> {
    if (this.exporting) {
      return;
    }

    const source = this.selected.length ? this.selected : this.filteredItems;
    if (!source.length) {
      this.isError = true;
      this.errorMessage = 'BUSES.CARDS_PDF_EMPTY';
      return;
    }

    this.exportingCards = true;
    this.isError = false;
    this.errorMessage = '';
    try {
      await this.busCardsPdfService.export({
        schoolName: this.schoolName,
        isArabic: this.languageService.current === 'ar',
        cutMarginMm: this.cutMarginMm,
        labels: {
          title: this.translate.instant('BUSES.PDF_CARD_TITLE'),
          number: this.translate.instant('BUSES.NUMBER'),
          driver: this.translate.instant('BUSES.DRIVER'),
          plate: this.translate.instant('BUSES.PLATE'),
          noQr: this.translate.instant('BUSES.PDF_NO_QR'),
        },
        buses: source
          .slice()
          .sort((a, b) => String(a.number || '').localeCompare(String(b.number || ''), undefined, { numeric: true }))
          .map(item => ({
            id: item.id,
            number: String(item.number || '').trim(),
            name: String(item.name || '').trim(),
            driverName: String(item.driverName || '').trim(),
            driverPhone: String(item.driverPhone || '').trim(),
            plateNumber: String(item.plateNumber || '').trim(),
            qrCode: String(item.qrCode || '').trim(),
          })),
      });
    } catch {
      this.isError = true;
      this.errorMessage = 'BUSES.CARDS_PDF_FAILED';
    } finally {
      this.exportingCards = false;
    }
  }

  openDetails(item: any) {
    if (!item?.id) {
      return;
    }
    this.router.navigate(portalCommands(this.router, 'settings', 'buses', item.id));
  }

  resetForm(item?: any) {
    this.form.reset({
      name: item?.name || '',
      number: item?.number || '',
      plateNumber: item?.plateNumber || '',
      driverName: item?.driverName || '',
      driverPhone: item?.driverPhone || '',
      capacity: item?.capacity ?? null,
    });
  }

  onAdd() {
    this.mode = 'add';
    this.selectedId = '';
    this.resetForm();
    this.isError = false;
    this.open = true;
  }

  onEdit() {
    const item = this.selected[0];
    this.mode = 'edit';
    this.selectedId = item.id;
    this.resetForm(item);
    this.isError = false;
    this.open = true;
  }

  onDeleteMany() {
    this.deleteModalBody = this.selected.length === 1 ? 'BUSES.DELETE_ONE' : 'BUSES.DELETE_MANY';
    this.deleteModalParams = this.selected.length === 1
      ? { name: this.selected[0].name }
      : { count: this.selected.length };
    this.showDeleteModal = true;
  }

  async onDeleteConfirm(confirmed: boolean) {
    if (confirmed) {
      for (const item of this.selected) {
        try {
          await this.busesService.delete(item.id);
        } catch (error: any) {
          this.isError = true;
          this.errorMessage = error?.error?.errorMessages?.[0]?.message || 'BUSES.DELETE_FAILED';
          this.showDeleteModal = false;
          await this.fetchItems();
          return;
        }
      }
      this.selected = [];
      await this.fetchItems();
    }
    this.showDeleteModal = false;
  }

  async submit() {
    if (this.form.invalid) {
      this.isError = true;
      this.errorMessage = 'COMMON.REQUIRED_FIELDS';
      return;
    }
    const raw = this.form.getRawValue();
    const body: Record<string, unknown> = {
      name: raw.name,
      number: raw.number,
      plateNumber: raw.plateNumber || undefined,
      driverName: raw.driverName || undefined,
      driverPhone: raw.driverPhone || undefined,
    };
    if (raw.capacity != null && raw.capacity !== ('' as any)) {
      body.capacity = Number(raw.capacity);
    }
    try {
      if (this.mode === 'edit') {
        await this.busesService.update(this.selectedId, body);
      } else {
        await this.busesService.create(body);
      }
      this.open = false;
      this.selected = [];
      await this.fetchItems();
    } catch (error: any) {
      this.isError = true;
      this.errorMessage = error?.error?.errorMessages?.[0]?.message || 'BUSES.SAVE_FAILED';
    }
  }

  closeModal() { this.open = false; this.isError = false; }
}
